import { useState } from "react";
import { getAuthHeader } from "../../../utils/getAuthHeader";
import CsvDropZone from "../CsvDropZone";
import UploadResultModal, { UploadResultData } from "../UploadResultModal";
import { API_BASE_ADMIN, CLIENT_ERROR_MESSAGES, SERVER_ERROR_MESSAGES, showErrorToast } from "./types";

interface Props {
  onSuccess: () => void;
}

const SAMPLE_CSV = [
  "farm_id,device_id",
  "FARM-0042,7831",
  "FARM-0089,2204",
  "FARM-0113,5590",
].join("\n");

function downloadBlob(content: string, filename: string, type = "text/csv") {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
}

export default function CsvUploadCard({ onSuccess }: Props) {
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResultData | null>(null);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await fetch(
        `${API_BASE_ADMIN}/iot_endpoints/iot-farm-mappings/bulk/csv`,
        { method: "POST", headers: { ...getAuthHeader() }, body: formData }
      );
      const data = await res.json().catch(() => ({}));

      if (res.status >= 400 && res.status < 500) {
        showErrorToast(CLIENT_ERROR_MESSAGES[(data as any).error] || (data as any).message || "Upload failed.");
        return;
      }
      if (res.status >= 500) {
        showErrorToast(SERVER_ERROR_MESSAGES[(data as any).error] || "An unexpected error occurred. Please try again.");
        return;
      }

      setResult({
        inserted: (data as any).inserted ?? 0,
        failed: (data as any).failed ?? 0,
        errors: (data as any).errors || [],
      });
      onSuccess();
    } catch {
      showErrorToast("Upload failed. Please check your connection and try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="card mb-4 shadow-sm">
        <div className="card-body">
          <p className="fw-semibold mb-3 text-muted" style={{ fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Bulk Upload via CSV
          </p>
          <CsvDropZone
            pendingFile={pendingFile}
            uploading={uploading}
            description={<>Required columns: <code>farm_id</code>, <code>device_id</code> &nbsp;·&nbsp; Max 500 rows</> as any}
            onFileSelect={setPendingFile}
            onConfirmUpload={() => {
              if (!pendingFile) return;
              const file = pendingFile;
              setPendingFile(null);
              handleUpload(file);
            }}
            onCancelFile={() => setPendingFile(null)}
            onDownloadSample={() => downloadBlob(SAMPLE_CSV, "iot_farm_mapping_sample.csv")}
          />
        </div>
      </div>

      {result && (
        <UploadResultModal
          result={result}
          onClose={() => setResult(null)}
        />
      )}
    </>
  );
}
